import React from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Button, IconButton, MenuItem, TextField, Tooltip, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useAuth } from "../../../common/auth";
import { TrafficResponse, getTraffic } from "../../../common/requests/platform/endpoints";
import { C } from "../../../common/theme";
import { Banner, KpiCard, KpiStrip, LoadingRows, Panel, Section } from "../../../ui";
import { RANGE_OPTIONS, RangeKey, change, formatDate, parseDateInput, toDateInput } from "../../Sales/salesData";
import {
  TrafficWindow,
  browserTimeZone,
  deviceLabel,
  errorMessage,
  formatClock,
  formatCount,
  formatMs,
  trafficWindow,
} from "../platformData";
import CountList, { CountRow } from "./CountList";
import RecentViews from "./RecentViews";
import TrafficChart from "./TrafficChart";
import { spinSx } from "./AppStatusSection";

const DEFAULT_RANGE: RangeKey = "30d";

const sectionGrid = (columns: Record<string, string>) => ({ display: "grid", gap: 1.25, gridTemplateColumns: columns });

function rows<T extends { views: number; sessions: number }>(items: T[], label: (item: T) => string): CountRow[] {
  return items.map((item) => ({ key: label(item), label: label(item), views: item.views, sessions: item.sessions }));
}

/** "Last 30 days · 5 Sep 2026 – 4 Oct 2026"; custom ranges already read as dates. */
function periodLabel(period: TrafficWindow, data: TrafficResponse) {
  const first = parseDateInput(data.daily[0]?.date);
  const last = parseDateInput(data.daily[data.daily.length - 1]?.date);
  if (!first || !last || period.label.includes("–")) return period.label;
  const span = first.getTime() === last.getTime() ? formatDate(first) : `${formatDate(first)} – ${formatDate(last)}`;
  return `${period.label} · ${span}`;
}

export default function TrafficSection() {
  const { idToken } = useAuth();
  const tokenRef = React.useRef(idToken);
  tokenRef.current = idToken;
  const hasToken = !!idToken;
  const tz = React.useMemo(browserTimeZone, []);

  /* range, kept in the URL like Sales */
  const [searchParams, setSearchParams] = useSearchParams();
  const rawRange = searchParams.get("range");
  const rangeKey = (RANGE_OPTIONS.some((r) => r.key === rawRange) ? rawRange : DEFAULT_RANGE) as RangeKey;
  const from = searchParams.get("from") ?? undefined;
  const to = searchParams.get("to") ?? undefined;

  const setRange = (range: RangeKey, nextFrom?: string, nextTo?: string) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        ["range", "from", "to"].forEach((k) => next.delete(k));
        if (range !== DEFAULT_RANGE) next.set("range", range);
        if (range === "custom") {
          if (nextFrom) next.set("from", nextFrom);
          if (nextTo) next.set("to", nextTo);
        }
        return next;
      },
      { replace: true }
    );

  const handleRange = (range: RangeKey) => {
    if (range !== "custom") return setRange(range);
    // Start the custom range from what's on screen.
    const current = trafficWindow(rangeKey, from, to);
    setRange("custom", toDateInput(current.start), toDateInput(new Date(current.end.getTime() - 1)));
  };

  /* data */
  const [data, setData] = React.useState<TrafficResponse | null>(null);
  const [shownWindow, setShownWindow] = React.useState<TrafficWindow | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = React.useState<Date | null>(null);
  const requestRef = React.useRef<AbortController | null>(null);

  const load = React.useCallback(async () => {
    const token = tokenRef.current;
    if (!token) return;
    requestRef.current?.abort();
    const ac = new AbortController();
    requestRef.current = ac;
    const period = trafficWindow(rangeKey, from, to);
    setLoading(true);
    setError(null);
    try {
      const res = await getTraffic(token, { start: period.start.toISOString(), end: period.end.toISOString(), tz }, ac.signal);
      if (ac.signal.aborted) return;
      setData(res.data);
      setShownWindow(period);
      setUpdatedAt(new Date());
    } catch (e: any) {
      if (ac.signal.aborted) return;
      setError(errorMessage(e, "Couldn't load traffic"));
    } finally {
      if (requestRef.current === ac) setLoading(false);
    }
  }, [rangeKey, from, to, tz]);

  React.useEffect(() => {
    if (hasToken) load();
  }, [hasToken, load]);
  React.useEffect(() => () => requestRef.current?.abort(), []);

  const breakdowns = React.useMemo(
    () =>
      data && {
        pages: rows(data.top_pages, (r) => r.path),
        referrers: rows(data.top_referrers, (r) => r.referrer),
        countries: rows(data.countries, (r) => r.country),
        devices: rows(data.devices, (r) => deviceLabel(r.device)),
        browsers: rows(data.browsers, (r) => r.browser),
      },
    [data]
  );

  const s = data?.summary;
  const deltaLabel = "vs prev. period";

  const filters = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", width: { xs: "100%", sm: "auto" } }}>
      <TextField
        select
        size="small"
        label="Date range"
        value={rangeKey}
        onChange={(e) => handleRange(e.target.value as RangeKey)}
        sx={{ width: { xs: "100%", sm: 170 } }}
      >
        {RANGE_OPTIONS.map((r) => (
          <MenuItem key={r.key} value={r.key}>
            {r.label}
          </MenuItem>
        ))}
      </TextField>
      {rangeKey === "custom" && (
        <>
          <TextField
            type="date"
            size="small"
            label="From"
            value={from ?? ""}
            onChange={(e) => setRange("custom", e.target.value, to)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ width: { xs: "calc(50% - 4px)", sm: 150 } }}
          />
          <TextField
            type="date"
            size="small"
            label="To"
            value={to ?? ""}
            onChange={(e) => setRange("custom", from, e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ width: { xs: "calc(50% - 4px)", sm: 150 } }}
          />
        </>
      )}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: { xs: "auto", sm: 0 } }}>
        {updatedAt && (
          <Typography variant="caption" sx={{ whiteSpace: "nowrap", color: C.muted }}>
            Updated {formatClock(updatedAt)}
          </Typography>
        )}
        <Tooltip title="Reload traffic">
          <span>
            <IconButton onClick={load} disabled={loading || !hasToken} aria-label="Reload traffic" sx={{ width: 44, height: 44 }}>
              <RefreshIcon fontSize="small" sx={loading ? spinSx : undefined} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <>
      <Section title="Traffic" subtitle={`Page views on inflos.co, counted by day in ${tz}`} actions={filters}>
        {error && (
          <Banner
            tone="danger"
            action={
              <Button variant="outlined" size="small" onClick={load} disabled={loading}>
                Retry
              </Button>
            }
          >
            {error}
          </Banner>
        )}

        {data && s && breakdowns && shownWindow ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, opacity: loading ? 0.6 : 1, transition: "opacity 120ms" }}>
            <Typography variant="overline" sx={{ color: C.muted }}>
              {periodLabel(shownWindow, data)}
            </Typography>

            <KpiStrip max={5}>
              <KpiCard
                title="Page views"
                value={formatCount(s.views)}
                delta={change(s.views, s.previous.views)}
                deltaLabel={deltaLabel}
                sub={`prev. ${formatCount(s.previous.views)}`}
                help="Every page shown on inflos.co, including in-app navigation. The previous period is the same length, immediately before."
              />
              <KpiCard
                title="Sessions"
                value={formatCount(s.sessions)}
                delta={change(s.sessions, s.previous.sessions)}
                deltaLabel={deltaLabel}
                sub={`prev. ${formatCount(s.previous.sessions)}`}
                help="Distinct browser sessions (one per tab visit). A session that spans midnight counts once here and on both days in the chart."
              />
              <KpiCard
                title="Visitors"
                value={formatCount(s.visitors)}
                sub={`${formatCount(s.signed_in_visitors)} signed in · ${formatCount(s.anonymous_sessions)} anonymous`}
                help="Signed-in users count once each, however many devices they use. Anonymous visitors can't be told apart, so each session that never signed in counts as one."
              />
              <KpiCard
                title="Today"
                value={formatCount(s.today_views)}
                sub="views since midnight"
                help={`Page views since midnight (${tz}), whatever date range is selected.`}
              />
              <KpiCard
                title="Avg page load"
                value={formatMs(s.avg_page_load_ms)}
                sub={s.median_page_load_ms !== null ? `median ${formatMs(s.median_page_load_ms)}` : "no timings"}
                help="Browser-measured time to the load event of the first page of a visit (in-app navigation repeats it). Timings over 60s are ignored."
              />
            </KpiStrip>

            <Box sx={sectionGrid({ xs: "minmax(0, 1fr)", lg: "minmax(0, 2fr) minmax(0, 1fr)" })}>
              <TrafficChart daily={data.daily} totals={{ views: s.views, sessions: s.sessions }} />
              <CountList title="Top pages" rows={breakdowns.pages} total={s.views} />
            </Box>

            <Box sx={sectionGrid({ xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" })}>
              <CountList
                title="Referrers"
                rows={breakdowns.referrers}
                total={s.views}
                help="The site a visit came from, by host. Direct = no referrer (typed, bookmarked, or from an app)."
              />
              <CountList title="Countries" rows={breakdowns.countries} total={s.views} />
              <CountList title="Devices" rows={breakdowns.devices} total={s.views} />
              <CountList title="Browsers" rows={breakdowns.browsers} total={s.views} />
            </Box>
          </Box>
        ) : !error ? (
          <Panel padding={0}>
            <LoadingRows rows={6} height={52} />
          </Panel>
        ) : null}
      </Section>

      {data && shownWindow && <RecentViews rows={data.recent} start={data.range.start} end={data.range.end} live={shownWindow.live} />}
    </>
  );
}
